<template class="GlobalAddressValidation">
    <v-form
        id="gav"
        ref="addressSelectForm"
        v-model="formValid"
        name="gavForm"
        class="validator-form"
    >
        <h1 class="heading">
            {{ translate("gav.addressSelect") }}
        </h1>
        <h2 class="alert-line">
            <ns-icon icon-name="icon-error-color-filled" color="#B2140A" style="width:1.5em;height:1.5em" />
            <span class="ml-2">{{ translate("gav.validationAlert") }}</span>
        </h2>
        <p class="address-verification-context">
            {{ translate("gav.addressVerificationContext") }}
        </p>
        <v-divider class="divider-1" />
        <div class="original-address-container">
            <div
                v-if="Array.isArray(suggestedAddresses) && suggestedAddresses.length >= 0"
                class="font-weight-regular radio-buttons"
            >
            

                <v-radio-group v-model="selectedAddress">
                    <v-radio :value="normalizedAddress" data-testid="originalAddress">
                        <template #label class="select-address">
                            <div>{{ translate("gav.original") }}</div>
                            <formatted-address :address="normalizedAddress" />
                        </template>
                    </v-radio>



                    

                    <v-radio v-if="Array.isArray(suggestedAddresses) && suggestedAddresses.length > 0"

                             :value="suggestedAddresses[0]"
                             data-testid="firstSuggestedAddress"
                    >
                        <template #label class="select-address">
                            <div>{{ translate("gav.suggested") }}</div>
                            <formatted-address :address="suggestedAddresses[0]" />
                        </template>
                    </v-radio>
                    <div class="additional-address-suggestions" v-if="Array.isArray(suggestedAddresses) && suggestedAddresses.length > 1">
                        <div>{{ translate("gav.alsoSuggested") }}</div>
                        <div
                            v-for="(item, i) in suggestedAddresses.slice(1)"
                            :key="i"
                        >
                            <v-radio
                                :value="item"
                                data-testid="selectedAddress"
                            >
                                <template #label>
                                    <formatted-address :address="item" />
                                </template>
                            </v-radio>
                        </div>
                    </div>
                </v-radio-group>
            </div>
            <div class="button-container" v-show="!partialType">
                <button
                    class="cancel-button link"
                    @click.prevent.stop="
                        () => {
                            addressClose();
                            selectedAddress = null;
                        }
                    "
                    data-testid="cancelButton"
                >
                    {{ translate("gav.cancel") }}
                </button>
                <button
                    class="primary-solid"
                    @click.prevent.stop="confirmSelectedAddress"
                    data-testid="confirmAddress"
                    :disabled = "!this.selectedAddress"
                >
                    {{ translate("gav.confirmAddress") }}
                </button>
            </div>
        </div>
        <div
            v-if="Array.isArray(suggestedAddresses) && suggestedAddresses.length < 1"
            class="font-weight-regular"
        >
            {{ translate("gav.noSuggestionsFound") }}
        </div>
        <div v-if="partialType">
            <partial-selection
                :error="partialSubmitted && partialError"
                :translations="partialSelectionText"
                :partialSuggestions="partialSuggestions"
                @clear-error="clearPartialError"
                @partial-address-selected="onPartialAddressSelected"
            />
        </div>
        <div v-if="busy" class="spinner-container" data-testid="busySpinner">
            <NsSpinner class="spinner" />
        </div>
    </v-form>
</template>

<script>
import { VRadio, VRadioGroup, VForm, VDivider } from "vuetify/lib";
import axios from "axios";
import { NsSpinner } from '@nuskin/design-components';
import { NsIcon } from '@nuskin/ns-icon';
import PartialSelection from './PartialSelection.vue';

import translationMixin from "../../../../mixins/translationMixin.js";
import urlBuilderMixin from "../../../../mixins/urlBuilderMixin.js";
import addressMixin from "../../../../mixins/addressMixin.js";
import authMixin from "../../../../mixins/authMixin.js";
import FormattedAddress from "./FormattedAddress.vue"

export default {
    components: {
        FormattedAddress,
        VRadio,
        VRadioGroup,
        VForm,
        VDivider,
        NsIcon,
        NsSpinner,
        PartialSelection,
    },
    mixins: [translationMixin('address-validation'), addressMixin, urlBuilderMixin, authMixin],
    props: {
        /** The address that Gav will return address suggestions for */
        inputAddress: {
            type: Object,
            default: null,
        },
        /**
         * A distinct implementation is needed for `callingModule`, because there
         * are several ways in which address object keys are formatted. The
         * standard naming convention is address.address1, etc. However, some repos
         * use address.shippingAddress1, etc. This requires a prop to allow the GAV
         * component to discern whether or not the address naming convention is
         * standard.
         * The value of "defaultCallingModule" signifies standard naming
         * ("address1", etc.)
         * The values of "profile" or "webcomponents-ui" will have the component
         * seed "shippingAddress1".
         * Any other naming convention needs to be implemented into addressMixin.js
         * to work properly.
         */
        callingModule: {
            type: String,
            required: true,
        },
    },
    data: function() {
        return {
            busy: false,
            formValid: true,
            radioGroup: null,
            selectedAddress: null,
            suggestedAddresses: {},
            enablePartialAddresses: false,
            showPartialSuggestions: false,
            partialError: false,
            partialSubmitted: false,
            partialSuggestions: [],
            partialType: null,
        };
    },
    computed: {
        country: function() {
            return (this.inputAddress && this.inputAddress.country) || 'US';
        },
        normalizedAddress() {
            return this.normalizeAddress(this.inputAddress, this.callingModule);
        },
        partialSelectionText() {
            return {
                displayAllPartials: this.translate('gav.displayAllPartials'),
                confirmMessage: this.partialType === 'StreetPartial' ?
                    this.translate('gav.confirmHouseNumber') :
                    this.translate('gav.confirmUnitNumber'),
                partialInputLabel: this.partialType === 'StreetPartial' ?
                    this.translate('gav.houseNumber') :
                    this.translate('gav.unitNumber'),
                noSuggestionsFound: this.translate('gav.noSuggestionsFound'),
                submitLabel: this.translate('gav.submitLabel'),
            };
        },
    },
    methods: {
        getDomain() {
            return window.location.hostname === "www.nuskin.com"
                ? "apis.nuskin.com"
                : "apis.test.nuskin.com";
        },
        buildContextualURL(domain, country) {
            if (typeof country === "string" && domain) {
                const params = this.getQueryString(country);

                return `https://${domain}/street-address/validate/v1/${country}${params}`;
            }
            return null;
        },
        async getSuggestions(inputAddress) {
            const normalizedAddress = this.normalizeAddress(
                inputAddress,
                this.callingModule,
            );
            const domain = this.getDomain();
            const country = normalizedAddress.country;
            const config = {
                method: "POST",
                url: this.buildContextualURL(domain, country),
                headers: {
                    client_id: this.getClientId(),
                },
                data: normalizedAddress,
            };

            try {
                this.busy = true;
                const response = await axios(config)
                    .then(result => {
                        return result.data;
                    })
                    .catch(error => {
                        const result = { data: {} };
                        if (error && error.config) {
                            result.data.message = "Max retries reached.";
                        } else {
                            result.data.message = "Request timeout.";
                        }
                        return result;
                    });

                const candidates = response && response.candidates;

                let suggestedAddresses = [];
                let exactMatch = false;
                let partialType = '';
                let partials = [];

                // Only update modal content if there are suggested addresses
                if (candidates && candidates.length > 0) {
                    suggestedAddresses = candidates;
                    exactMatch = this.isAddressExactMatch(
                        suggestedAddresses,
                        normalizedAddress,
                    );
                }
                const analysis = response && response.analysis || {};
                // StreetPartial and PremisesPartial require picklist
                if ((analysis.verificationStatus === 'StreetPartial'
                    || analysis.verificationStatus === 'PremisesPartial')
                    && (response.picklist && response.picklist.length > 0)
                ) {
                    partialType = analysis.verificationStatus;
                    partials = response.picklist;
                }

                const denormalizedAddresses = suggestedAddresses.map(a => this.denormalizeAddress(a, this.callingModule));

                return {
                    candidates,
                    denormalizedAddresses,
                    exactMatch,
                    partials,
                    partialType,
                };
            } catch (err) {
                /* istanbul ignore next */
                // eslint-disable-next-line no-console
                console.error("Error fetching suggested addresses", err);
            } finally {
                this.busy = false;
            }
        },
        isAddressExactMatch(suggestedAddresses, input) {
            let exactMatch = false;
            if (suggestedAddresses && input && suggestedAddresses.length === 1) {
                const suggestedAddress = suggestedAddresses[0];

                const inputKeys = Object.keys(input || {});
                const suggestionKeys = Object.keys(suggestedAddress || {});
                const allKeys = [...new Set(inputKeys.concat(suggestionKeys))];

                // Compare each address field in input vs suggestion.
                exactMatch = allKeys.every((key) => {
                    if (typeof input[key] !== typeof suggestedAddress[key]) {
                        // Input and suggestion have different data types in this field.
                        return false;
                    }

                    if (typeof suggestedAddress[key] !== 'string') {
                        // Field is not a string; skip it.
                        return true;
                    }

                    // Field is determined to be a string in both input and suggestion.
                    // Compare field values, ignoring case sensitivity.
                    return input[key].toLowerCase() === suggestedAddress[key].toLowerCase();
                });
            }
            return exactMatch;
        },
        async loadSuggestions(inputAddress) {
            this.suggestedAddresses = null;
            const result = await this.getSuggestions(inputAddress);
            if (result.exactMatch && result.candidates) {
                this.emitAddressConfirmed(result.candidates[0]);
            } else if (result.partialType !== '') {
                this.partialError = true;
                this.partialSuggestions = result.partials;
                this.partialType = result.partialType;
            } else {
                this.partialType = null;
                this.suggestedAddresses = result.candidates;
                this.selectedAddress = Array.isArray(this.suggestedAddresses) && this.suggestedAddresses.length > 0 ? this.suggestedAddresses[0] : null;
            }

            return result;
        },
        emitAddressConfirmed(address) {
            this.$emit("confirm-address", this.denormalizeAddress(address, this.callingModule));
            this.resetState();
        },
        addressClose() {
            this.$emit("toggle-gav");
            this.resetState();
        },
        resetState() {
            this.busy = false;
            this.formValid = true;
            this.radioGroup = null;
            this.selectedAddress = null;
            this.suggestedAddresses = {};
            this.enablePartialAddresses = false;
            this.showPartialSuggestions = false;
            this.partialError = false;
            this.partialSubmitted = false;
            this.partialSuggestions = [];
            this.partialType = null;
        },
        confirmSelectedAddress() {
            this.emitAddressConfirmed(this.selectedAddress);
        },
        clearPartialError() {
            this.partialError = false;
        },
        async onPartialAddressSelected(value) {
            this.partialSubmitted = true;

            let adjustedAddress;
            if (this.partialType === 'StreetPartial') {
                adjustedAddress = this.streetPartial(value);
            } else {
                adjustedAddress = this.premisesPartial(value);
            }
            const denormalized = this.denormalizeAddress(adjustedAddress);
            this.inputAddress = denormalized;
            await this.loadSuggestions(denormalized);
        },
        streetPartial(value) {
            const address = this.normalizedAddress;
            let lone = address.street;
            let ltwo = address.street2;

            const urbregex = /^(urb|urbanisation|urbanizaci[óo]n)/i;
            if (urbregex.test(lone)) {
                ltwo = value + " " + ltwo;
                address.street2 = ltwo;
            } else {
                lone = value + " " + lone.replace(/^[0-9]+/, "");
                address.street = lone;
            }
            return address;
        },
        premisesPartial(value) {
            const address = this.normalizedAddress;
            let lone = `${address.street.replace(/\s*APT\s*\d*/gi, '')} Apt ${value}`;
            address.street = lone;
            return address;
        },
    },
};
</script>

<style lang="scss" scoped>
@use "sass:map";
@use "~@nuskin/ns-core-styles/src/scss/colors";
@use "~@nuskin/ns-core-styles/src/scss/vuetifyOverrides";
@use "~@nuskin/ns-core-styles/src/scss/typographyVariables";
@use "~@nuskin/ns-core-styles/src/scss/global.mixins";

$color-error: map.get(colors.$baseColors, color-error);
$color-divider: map.get(colors.$baseColors, ash);

.validator-form {
    position: relative;
}

.heading {
    margin-top: 0;
    margin-bottom: 10px;
    @include global.heading-1();
}

.alert-line {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: $color-error;
    margin-top: 0;
    margin-bottom: 40px;
    @include global.heading-2();
}

.address-verification-context {
    margin-bottom: 20px;
    @include global.paragraph-l();
}

.address-heading {
    margin-bottom: 20px;
    @include global.heading-2();
}

.original-address-container,
.additional-address-suggestions {
    margin-bottom: 20px;

    .original-address {
        margin-bottom: 20px;
        @include global.paragraph-l();
    }

    ::v-deep .v-radio {
        align-items: flex-start;
        margin-bottom: 15px;

        .v-label {
            flex-direction: column;
            align-items: flex-start;
        }

        .v-icon.accent--text {
            color: map.get(colors.$baseColors, "shells") !important;
        }

        &:not(:last-child):not(:only-child) {
            margin-bottom: 15px;
        }
    }
}
.additional-address-suggestions {
    &> div:first-child {
        margin-bottom: 15px;
        font-weight: map.get(typographyVariables.$typography, "font-weight-semibold");
        font-size: 16px;
    }
 }

.address-text {
    font-style: normal;
    @include global.paragraph-l();
}

.divider-1 {
    background-color: $color-divider;
    margin-bottom: 20px;
}

.button-container {
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
    justify-content: flex-end;
    margin-top: 20px;

    button {
        width: 285px;
    }

    .cancel-button {
        width: auto;
        margin-bottom: 15px;
        margin-right: 15px;
    }
}

@media screen and (min-width: 640px) {
    .button-container {
        flex-direction: row;

        .cancel-button {
            margin-bottom: 0;
        }
    }
}

.spinner-container {
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(220,220,220,0.75);
}
</style>
